import React, { useRef } from 'react'
import { makeStyles } from '@mui/styles'

import PuiDialog, { BasePuiDialogProps } from '../dialog/PuiDialog'
import BaseSignature, {
  BaseSignatureHandle,
  BaseSignatureProps,
} from './BaseSignature'
import FreeNameSignature from './FreeNameSignature'

const useStyles = makeStyles(
  theme => ({
    paper: {
      overflowY: 'visible',
      paddingTop: theme.spacing(3),
      userSelect: 'none',
    },
  }),
  { name: 'SignatureDialog' },
)

export interface SignatureDialogProps
  extends BaseSignatureProps,
  BasePuiDialogProps {
  readonly SignatureComponent?: typeof BaseSignature
  readonly onClose?: () => void
  readonly onSign: (signer: string, value: string) => void
  readonly showSkip?: boolean
}

const SignatureDialog = ({
  onClose,
  open,
  SignatureComponent = FreeNameSignature,
  onSign,
  ...rest
}: SignatureDialogProps) => {
  const classes = useStyles()

  const signatureComponentRef = useRef<BaseSignatureHandle>(null)

  const handleSign = (signer: string, value: string) => {
    onSign(signer, value)
    if (onClose) {
      onClose()
    }
  }

  const hasUnsavedChanges = () =>
    signatureComponentRef.current?.hasUnsavedChanges() ?? false

  const onOk = () => {
    signatureComponentRef.current?.trySign()
  }

  return (
    <PuiDialog
      confirmSaveOnClose
      ConfirmCloseDialogProps={{
        onOk,
      }}
      aria-labelledby="signature-dialog"
      classes={{
        paper: classes.paper,
      }}
      hasUnsavedChanges={hasUnsavedChanges}
      open={open}
      onClose={onClose}
    >
      <SignatureComponent
        ref={signatureComponentRef}
        onClose={onClose}
        onSign={handleSign}
        {...rest}
      />
    </PuiDialog>
  )
}

export default SignatureDialog
